import React, { useState, useRef, useCallback } from "react";
import { Mic, Square, Loader2 } from "lucide-react";

export default function VoiceRecorder({ onRecordingComplete, disabled = false, size = "default" }) {
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Try different mime types for cross-browser compatibility
      // iOS Safari prefers mp4/aac, Chrome/Firefox prefer webm/opus
      let mimeType = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "audio/webm";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/mp4";
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ""; // Let browser choose default
          }
        }
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      chunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        // Determine the actual mime type used
        const actualType = mediaRecorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: actualType });
        onRecordingComplete?.(blob);
        setDuration(0);
      };

      mediaRecorder.start();
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch {
      // Mic access denied or not available
    }
  }, [onRecordingComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  }, [recording]);

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const isSmall = size === "small";
  const btnSize = isSmall ? "w-10 h-10" : "w-14 h-14";
  const iconSize = isSmall ? "w-4 h-4" : "w-5 h-5";

  return (
    <div className="flex items-center gap-3" data-testid="voice-recorder">
      <button
        onClick={recording ? stopRecording : startRecording}
        disabled={disabled}
        data-testid={recording ? "stop-recording-btn" : "start-recording-btn"}
        className={`${btnSize} rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
          recording
            ? "bg-red-500 hover:bg-red-600 text-white shadow-[0_0_20px_rgba(239,68,68,0.4)]"
            : "bg-[#00FF88] hover:bg-[#00CC6A] text-black shadow-[0_0_15px_rgba(0,255,136,0.3)]"
        } disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {recording ? (
          <Square className={iconSize} fill="currentColor" />
        ) : (
          <Mic className={iconSize} />
        )}
      </button>
      {recording && (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm text-red-400 font-mono tabular-nums" data-testid="recording-duration">
            {formatDuration(duration)}
          </span>
        </div>
      )}
    </div>
  );
}
